/**
 * Session-only extras for saved filters.
 * The user-filter API stores markets, product types, health authorities, and dates.
 * Category, sub-category, topics, and favorite stay here so the panel can round-trip
 * until those fields exist on the API. A failed create is not stored here.
 */

import dayjs from "dayjs";
import type { UserFilterCriteria } from "../../../services/regulationsService";
import {
  listCriteriaToFilterSelection,
  mapFilterSelectionToListCriteria,
  type FilterSelectionState,
} from "./regulationFilterDraft";

const EXTRAS_KEY = "gri.savedFilterExtras";
const LOCAL_KEY = "gri.localSavedFilters";
const ORDER_KEY = "gri.savedFilterOrder";
const NAME_KEY = "gri.savedFilterNames";

export interface LocalSavedFilter {
  id: number;
  name: string;
  selection: FilterSelectionState;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function readSavedFilterExtras(id: number): FilterSelectionState | null {
  const map = readJson<Record<string, FilterSelectionState>>(EXTRAS_KEY, {});
  return map[String(id)] ?? null;
}

export function writeSavedFilterExtras(id: number, selection: FilterSelectionState) {
  const map = readJson<Record<string, FilterSelectionState>>(EXTRAS_KEY, {});
  map[String(id)] = selection;
  writeJson(EXTRAS_KEY, map);
}

export function deleteSavedFilterExtras(id: number) {
  const map = readJson<Record<string, FilterSelectionState>>(EXTRAS_KEY, {});
  delete map[String(id)];
  writeJson(EXTRAS_KEY, map);
}

export function readLocalSavedFilters(): LocalSavedFilter[] {
  return readJson<LocalSavedFilter[]>(LOCAL_KEY, []);
}

export function rememberLocalSavedFilter(
  name: string,
  selection: FilterSelectionState,
): LocalSavedFilter {
  const created: LocalSavedFilter = {
    id: -Date.now(),
    name,
    selection,
  };
  writeJson(LOCAL_KEY, [...readLocalSavedFilters(), created]);
  writeSavedFilterExtras(created.id, selection);
  return created;
}

export function updateLocalSavedFilter(
  id: number,
  name: string,
  selection: FilterSelectionState,
) {
  const next = readLocalSavedFilters().map((filter) =>
    filter.id === id ? { ...filter, name, selection } : filter,
  );
  writeJson(LOCAL_KEY, next);
  writeSavedFilterExtras(id, selection);
}

export function removeLocalSavedFilter(id: number) {
  writeJson(
    LOCAL_KEY,
    readLocalSavedFilters().filter((filter) => filter.id !== id),
  );
  deleteSavedFilterExtras(id);
}

export function readSavedFilterOrder(): number[] {
  return readJson<number[]>(ORDER_KEY, []);
}

export function writeSavedFilterOrder(ids: number[]) {
  writeJson(ORDER_KEY, ids);
}

export function readSavedFilterNameOverride(id: number): string | null {
  const map = readJson<Record<string, string>>(NAME_KEY, {});
  return map[String(id)] ?? null;
}

export function writeSavedFilterNameOverride(id: number, name: string) {
  const map = readJson<Record<string, string>>(NAME_KEY, {});
  map[String(id)] = name;
  writeJson(NAME_KEY, map);
}

export function deleteSavedFilterNameOverride(id: number) {
  const map = readJson<Record<string, string>>(NAME_KEY, {});
  delete map[String(id)];
  writeJson(NAME_KEY, map);
}

export function selectionToCriteria(
  selection: FilterSelectionState,
): UserFilterCriteria {
  const mapped = mapFilterSelectionToListCriteria(selection);
  const criteria: UserFilterCriteria = {};
  if (mapped.markets.length > 0) {
    criteria.markets = mapped.markets.map((item) => item.code);
  }
  if (mapped.productTypes.length > 0) {
    criteria.product_types = mapped.productTypes.map((item) => item.code);
  }
  if (mapped.healthAuthorities.length > 0) {
    criteria.ha = mapped.healthAuthorities.map((item) => item.code);
  }
  if (mapped.tags.length > 0) {
    criteria.tags = mapped.tags.map((item) => item.code);
  }
  if (mapped.fromDate?.isValid()) {
    criteria.start_date = mapped.fromDate.format("YYYY-MM-DD");
  }
  if (mapped.toDate?.isValid()) {
    criteria.end_date = mapped.toDate.format("YYYY-MM-DD");
  }
  return criteria;
}

export function criteriaToSelection(
  criteria: UserFilterCriteria,
  extras: FilterSelectionState | null,
): FilterSelectionState {
  if (extras) return extras;
  const asConcepts = (codes?: string[]) =>
    (codes ?? []).map((code) => ({ id: code, code, name: code }));
  return listCriteriaToFilterSelection({
    markets: asConcepts(criteria.markets),
    productTypes: asConcepts(criteria.product_types),
    healthAuthorities: asConcepts(criteria.ha),
    tags: asConcepts(criteria.tags),
    fromDate: criteria.start_date ? dayjs(criteria.start_date) : null,
    toDate: criteria.end_date ? dayjs(criteria.end_date) : null,
    favorite: null,
  });
}

export function applySavedFilterOrder<T extends { id: number }>(items: T[]): T[] {
  const order = readSavedFilterOrder();
  if (order.length === 0) return items;
  const rank = new Map(order.map((id, index) => [id, index]));
  return [...items].sort((left, right) => {
    const leftRank = rank.get(left.id);
    const rightRank = rank.get(right.id);
    if (leftRank == null && rightRank == null) return 0;
    if (leftRank == null) return 1;
    if (rightRank == null) return -1;
    return leftRank - rightRank;
  });
}
